import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, TrendingUp, TrendingDown, ArrowLeftRight, Loader2, History, GitCompare } from "lucide-react";
import { api, fmtKES } from "@/lib/api";
import { signTone, toneClassName, signedPrefix } from "@/lib/numberColor";
import GlobalBackButton from "@/components/GlobalBackButton";
import { useSortableData } from "@/hooks/useSortableData";
import { SortableTh } from "@/components/ui/sortable-th";
import {
    LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
    ReferenceLine, CartesianGrid, Legend,
} from "recharts";

/**
 * Obondo Backtest Report — Phase 4.
 *
 * Lets an institutional trader (MoE / CMA) replay the Obondo signal engine over
 * the last N days on a chosen symbol, with realistic slippage/fees/spread.
 * Shows equity curve, KPI cards, and a full trade log so the trader can
 * pressure-test the strategy before flipping into semi-auto or autonomous.
 */
const DEFAULT_BASKET_PICKER = ["SCOM", "EQTY", "KCB", "EABL", "COOP", "ABSA", "BAT", "STANBIC", "NCBA", "SBIC", "BAMB", "JUB"];

export default function BacktestReport() {
    const location = useLocation();
    const [symbol, setSymbol] = useState("SCOM");
    const [basket, setBasket] = useState([]);       // for multi-symbol mode
    const [days, setDays] = useState(30);
    const [capital, setCapital] = useState(100_000);
    const [minConf, setMinConf] = useState(0.55);
    const [result, setResult] = useState(null);
    const [busy, setBusy] = useState(false);
    const [history, setHistory] = useState([]);
    const [compareWith, setCompareWith] = useState(null);   // full detail of the other run
    const { sorted: sortedHistory, sortKey: historySortKey, direction: historyDirection, requestSort: requestHistorySort } = useSortableData(history, {
        accessors: {
            date: (r) => r.created_at || "",
            symbol: (r) => r.symbol || "",
            days: (r) => Number(r.days || 0),
            min_conf: (r) => Number(r.min_confidence || 0),
            return_pct: (r) => Number(r.kpis?.total_return_pct || 0),
            sharpe: (r) => Number(r.kpis?.sharpe || 0),
            trades: (r) => Number(r.trades_count || 0),
        },
    });
    const [showHistory, setShowHistory] = useState(false);
    const [prefilledFromPortfolio, setPrefilledFromPortfolio] = useState(false);

    async function loadHistory() {
        try {
            const { data } = await api.get("/obondo/backtest/history?limit=20");
            setHistory(data.runs || []);
        } catch (err) {
            console.warn("[BacktestReport] history load failed:", err);
        }
    }

    useEffect(() => { loadHistory(); }, []);

    // ── /app/portfolio → /app/backtest?basket=SCOM,EQTY shortcut ─────────
    // Portfolio page hands us the user's current open-position tickers so we
    // can jump straight into a multi-symbol backtest without re-picking pills.
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const raw = params.get("basket");
        if (!raw) return;
        const symbols = raw
            .split(",")
            .map((s) => s.trim().toUpperCase())
            .filter(Boolean);
        if (symbols.length === 0) return;
        setBasket(symbols);
        setPrefilledFromPortfolio(true);
        toast.success(`Pre-filled ${symbols.length} symbol(s) from your portfolio — click Run backtest.`);
    }, [location.search]);

    // Union of the fixed picker + any symbols pre-filled from the portfolio
    // that don't already appear in the picker (keeps chips visible for them).
    const pickerSymbols = Array.from(new Set([...DEFAULT_BASKET_PICKER, ...basket]));

    async function runBacktest() {
        setBusy(true);
        setResult(null);
        setCompareWith(null);
        try {
            const multi = basket.length > 0;
            const { data } = multi
                ? await api.post("/obondo/backtest/multi", {
                    symbols: basket, days,
                    initial_capital: capital, min_confidence: minConf,
                  })
                : await api.post("/obondo/backtest/run", {
                    symbol, days,
                    initial_capital: capital, min_confidence: minConf,
                  });
            setResult({ ...data, is_multi: multi });
            if (!multi) loadHistory();
            toast.success(`Backtest complete — ${data.kpis.trades} trades executed`);
        } catch (e) {
            const detail = e?.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Backtest failed — try a different symbol or window.");
        } finally {
            setBusy(false);
        }
    }

    function toggleBasketSym(s) {
        setBasket((prev) => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]);
    }

    async function loadForCompare(runId) {
        try {
            const { data } = await api.get(`/obondo/backtest/${runId}`);
            setCompareWith(data);
            toast.success(`Comparing against ${data.symbol} · ${data.days}d · min ${data.min_confidence}`);
        } catch {
            toast.error("Couldn't load comparison run.");
        }
    }

    const kpis = result?.kpis;

    return (
        <div className="min-h-screen bg-background text-foreground p-4 sm:p-6" data-testid="backtest-report">
            <GlobalBackButton />
            <div className="max-w-6xl mx-auto space-y-6">
                <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center">
                        <Sparkles className="w-5 h-5" />
                    </div>
                    <div className="flex-1">
                        <div className="overline text-primary">Obondo Autopilot · Phase 4</div>
                        <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight">Backtest Report</h1>
                        <p className="text-sm text-muted-foreground mt-1">
                            Replay the Obondo signal engine over recent daily bars with realistic slippage,
                            spread, and commissions baked in. Deterministic per UTC day — repeat runs are stable.
                        </p>
                    </div>
                </div>

                {/* ─── Config panel ─── */}
                <div className="border border-border bg-card rounded-lg p-4 sm:p-5">
                    <div className="mb-3 text-[11px] text-muted-foreground">
                        <b>Single-symbol</b> mode uses the picker below. <b>Multi-symbol basket</b> mode: click tickers
                        to add them to the pill row. Empty basket → single-symbol mode.
                    </div>
                    {prefilledFromPortfolio && basket.length > 0 && (
                        <div
                            className="mb-3 rounded border border-primary/40 bg-primary/5 px-3 py-2 text-[11px] text-primary flex items-center gap-2 flex-wrap"
                            data-testid="backtest-prefilled-notice"
                        >
                            <span className="font-bold">Pre-filled from your portfolio:</span>
                            <span className="mono">{basket.join(" · ")}</span>
                            <button
                                onClick={() => { setBasket([]); setPrefilledFromPortfolio(false); }}
                                className="ml-auto underline hover:opacity-80"
                                data-testid="backtest-prefilled-clear"
                            >
                                Start fresh
                            </button>
                        </div>
                    )}
                    <div className="mb-3 flex flex-wrap gap-1.5" data-testid="backtest-basket-picker">
                        {pickerSymbols.map((s) => {
                            const on = basket.includes(s);
                            return (
                                <button key={s} onClick={() => toggleBasketSym(s)}
                                    className={`px-2 py-1 rounded text-[10px] font-semibold border transition-colors ${
                                        on ? "bg-primary text-primary-foreground border-primary"
                                           : "border-border text-muted-foreground hover:border-primary/50 hover:text-foreground"
                                    }`}
                                    data-testid={`backtest-basket-${s}`}
                                >{s}</button>
                            );
                        })}
                        {basket.length > 0 && (
                            <button onClick={() => setBasket([])}
                                className="px-2 py-1 rounded text-[10px] font-semibold border border-red-500/50 text-red-600 hover:bg-red-500/10"
                                data-testid="backtest-basket-clear"
                            >Clear basket</button>
                        )}
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3 items-end">
                        <div>
                            <label className="text-xs font-semibold text-muted-foreground">
                                Symbol {basket.length > 0 && <span className="text-primary">(ignored — using basket)</span>}
                            </label>
                            <select
                                value={symbol}
                                onChange={(e) => setSymbol(e.target.value)}
                                disabled={basket.length > 0}
                                className="mt-1 w-full h-9 rounded border border-border bg-background px-2 mono disabled:opacity-50"
                                data-testid="backtest-symbol"
                            >
                                {["SCOM", "EQTY", "KCB", "EABL", "COOP", "ABSA", "BAT", "BAMB", "JUB", "NCBA", "STANBIC"].map((s) => (
                                    <option key={s} value={s}>{s}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="text-xs font-semibold text-muted-foreground">Days</label>
                            <input
                                type="number" min={7} max={180} value={days}
                                onChange={(e) => setDays(Number(e.target.value) || 30)}
                                className="mt-1 w-full h-9 rounded border border-border bg-background px-2 mono"
                                data-testid="backtest-days"
                            />
                        </div>
                        <div>
                            <label className="text-xs font-semibold text-muted-foreground">Initial capital (KES)</label>
                            <input
                                type="number" min={1000} step={10000} value={capital}
                                onChange={(e) => setCapital(Number(e.target.value) || 100_000)}
                                className="mt-1 w-full h-9 rounded border border-border bg-background px-2 mono"
                                data-testid="backtest-capital"
                            />
                        </div>
                        <div>
                            <label className="text-xs font-semibold text-muted-foreground">Min. confidence</label>
                            <input
                                type="number" step="0.05" min={0.30} max={0.95} value={minConf}
                                onChange={(e) => setMinConf(Number(e.target.value) || 0.55)}
                                className="mt-1 w-full h-9 rounded border border-border bg-background px-2 mono"
                                data-testid="backtest-min-conf"
                            />
                        </div>
                        <button
                            onClick={runBacktest}
                            disabled={busy}
                            className="h-9 rounded bg-primary text-primary-foreground font-bold hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2"
                            data-testid="backtest-run"
                        >
                            {busy ? <><Loader2 className="w-4 h-4 animate-spin" />Running…</> : "Run backtest"}
                        </button>
                    </div>
                    {result?.cost_model && (
                        <div className="mt-3 text-[11px] text-muted-foreground border-t border-border pt-2 flex items-center justify-between flex-wrap gap-2">
                            <span>Cost model: fees {result.cost_model.fee_per_side_bps} bps/side ·
                                slippage {result.cost_model.base_slippage_bps} bps/side ·
                                half-spread {result.cost_model.half_spread_bps} bps</span>
                            <button
                                onClick={() => setShowHistory((v) => !v)}
                                className="flex items-center gap-1 text-primary underline"
                                data-testid="backtest-toggle-history"
                            >
                                <History className="w-3 h-3" /> {showHistory ? "Hide" : "Show"} history ({history.length})
                            </button>
                        </div>
                    )}
                </div>

                {/* History strip */}
                {showHistory && history.length > 0 && (
                    <div className="border border-border bg-card rounded-lg p-4">
                        <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-2">
                            <History className="w-3.5 h-3.5" /> Recent backtest runs
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-xs" data-testid="backtest-history-table">
                                <thead className="border-b border-border">
                                    <tr className="text-left text-muted-foreground">
                                        <SortableTh label="Date" sortKey="date" currentKey={historySortKey} direction={historyDirection} onSort={requestHistorySort} className="py-2 pr-2" />
                                        <SortableTh label="Symbol" sortKey="symbol" currentKey={historySortKey} direction={historyDirection} onSort={requestHistorySort} className="pr-2" />
                                        <SortableTh label="Days" sortKey="days" currentKey={historySortKey} direction={historyDirection} onSort={requestHistorySort} align="right" className="pr-2" />
                                        <SortableTh label="Min. conf" sortKey="min_conf" currentKey={historySortKey} direction={historyDirection} onSort={requestHistorySort} align="right" className="pr-2" />
                                        <SortableTh label="Return" sortKey="return_pct" currentKey={historySortKey} direction={historyDirection} onSort={requestHistorySort} align="right" className="pr-2" />
                                        <SortableTh label="Sharpe" sortKey="sharpe" currentKey={historySortKey} direction={historyDirection} onSort={requestHistorySort} align="right" className="pr-2" />
                                        <SortableTh label="Trades" sortKey="trades" currentKey={historySortKey} direction={historyDirection} onSort={requestHistorySort} align="right" className="pr-2" />
                                        <th>Compare</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortedHistory.map((r) => (
                                        <tr key={r.id} className="border-b border-border/50 last:border-0">
                                            <td className="py-1.5 pr-2 mono">{r.created_at?.slice(0, 16).replace("T", " ")}</td>
                                            <td className="pr-2 font-bold">{r.symbol}</td>
                                            <td className="pr-2 text-right mono">{r.days}</td>
                                            <td className="pr-2 text-right mono">{r.min_confidence}</td>
                                            <td className={`pr-2 text-right mono font-semibold ${toneClassName(signTone(r.kpis.total_return_pct))}`}>
                                                {r.kpis.total_return_pct}%
                                            </td>
                                            <td className="pr-2 text-right mono">{r.kpis.sharpe}</td>
                                            <td className="pr-2 text-right mono">{r.trades_count}</td>
                                            <td>
                                                <button
                                                    onClick={() => loadForCompare(r.id)}
                                                    className="text-primary underline text-[11px] flex items-center gap-1"
                                                    data-testid={`backtest-compare-${r.id}`}
                                                >
                                                    <GitCompare className="w-3 h-3" /> vs
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {kpis && (
                    <>
                        {/* ─── KPI cards ─── */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            <Kpi label="Total return" value={`${kpis.total_return_pct}%`}
                                 accent={toneClassName(signTone(kpis.total_return_pct))}
                                 icon={signTone(kpis.total_return_pct) === "neg" ? <TrendingDown /> : <TrendingUp />} />
                            <Kpi label="Max drawdown" value={`${kpis.max_dd_pct}%`} accent="text-red-500" />
                            <Kpi label="Sharpe ratio" value={kpis.sharpe.toString()}
                                 accent={kpis.sharpe >= 1 ? "text-emerald-500" : ""} />
                            <Kpi label="Win rate" value={`${kpis.win_rate_pct}%`}
                                 sub={`${kpis.wins}W · ${kpis.losses}L · ${kpis.trades} trades`} />
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            <Kpi label="Profit factor" value={String(kpis.profit_factor)} />
                            <Kpi label="Avg win" value={fmtKES(kpis.avg_win_kes)} accent="text-emerald-500" />
                            <Kpi label="Avg loss" value={fmtKES(kpis.avg_loss_kes)} accent="text-red-500" />
                            <Kpi label="Final equity" value={fmtKES(kpis.final_equity)} />
                        </div>

                        {/* ─── Equity curve ─── */}
                        <div className="border border-border bg-card rounded-lg p-4">
                            <div className="flex items-center justify-between mb-3">
                                <h3 className="font-semibold text-sm">
                                    Equity curve · {result.symbol}
                                    {compareWith && <span className="text-muted-foreground"> · vs {compareWith.symbol} ({compareWith.days}d, min {compareWith.min_confidence})</span>}
                                </h3>
                                {compareWith && (
                                    <button onClick={() => setCompareWith(null)}
                                        className="text-[11px] text-muted-foreground underline"
                                        data-testid="backtest-clear-compare"
                                    >Clear comparison</button>
                                )}
                            </div>
                            <div className="h-64" data-testid="backtest-equity-chart">
                                <ResponsiveContainer width="100%" height="100%">
                                    <LineChart data={_mergeCurves(result.bars, compareWith?.bars)}>
                                        <CartesianGrid stroke="#e5e7eb" strokeDasharray="3 3" />
                                        <XAxis dataKey="time" tick={{ fontSize: 10 }} hide={result.bars.length > 40} />
                                        <YAxis domain={["dataMin", "dataMax"]} tick={{ fontSize: 10 }} tickFormatter={(v) => (v / 1000).toFixed(0) + "k"} />
                                        <Tooltip formatter={(v, k) => k.startsWith("equity") ? fmtKES(v) : v} />
                                        <Legend />
                                        <ReferenceLine y={result.initial_capital} stroke="#94a3b8" strokeDasharray="5 5" label="start" />
                                        <Line type="monotone" dataKey="equity_current"  name="Current run" stroke="#4f46e5" strokeWidth={2} dot={false} />
                                        {compareWith && <Line type="monotone" dataKey="equity_compare" name="Comparison"  stroke="#f59e0b" strokeWidth={2} dot={false} strokeDasharray="4 4" />}
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>
                            {compareWith && (
                                <div className="grid grid-cols-2 gap-2 mt-3 text-xs">
                                    <CompareKpiRow label="Return"     a={result.kpis.total_return_pct + "%"} b={compareWith.kpis.total_return_pct + "%"} />
                                    <CompareKpiRow label="Sharpe"     a={result.kpis.sharpe}                b={compareWith.kpis.sharpe} />
                                    <CompareKpiRow label="Max DD"     a={result.kpis.max_dd_pct + "%"}      b={compareWith.kpis.max_dd_pct + "%"} />
                                    <CompareKpiRow label="Win rate"   a={result.kpis.win_rate_pct + "%"}    b={compareWith.kpis.win_rate_pct + "%"} />
                                </div>
                            )}
                        </div>

                        {/* ─── Trade log ─── */}
                        <div className="border border-border bg-card rounded-lg p-4">
                            <h3 className="font-semibold text-sm mb-3 flex items-center gap-2">
                                <ArrowLeftRight className="w-4 h-4" /> Trade log · {result.trades.length} trades
                            </h3>
                            <div className="overflow-x-auto">
                                <table className="w-full text-xs" data-testid="backtest-trade-log">
                                    <thead className="border-b border-border">
                                        <tr className="text-left text-muted-foreground">
                                            <th className="py-2 pr-2">Entry</th>
                                            <th className="pr-2">Exit</th>
                                            <th className="pr-2">Side</th>
                                            <th className="pr-2 text-right">Qty</th>
                                            <th className="pr-2 text-right">Entry px</th>
                                            <th className="pr-2 text-right">Exit px</th>
                                            <th className="pr-2 text-right">PnL (KES)</th>
                                            <th>Reason</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {result.trades.map((t) => (
                                            <tr key={`${t.entry_ts}-${t.exit_ts}-${t.side}`} className="border-b border-border/50 last:border-0">
                                                <td className="py-1.5 pr-2 mono">{t.entry_ts}</td>
                                                <td className="pr-2 mono">{t.exit_ts}</td>
                                                <td className={`pr-2 font-semibold ${t.side === "buy" ? "text-emerald-500" : "text-red-500"}`}>
                                                    {t.side.toUpperCase()}
                                                </td>
                                                <td className="pr-2 text-right mono">{t.qty}</td>
                                                <td className="pr-2 text-right mono">{t.entry}</td>
                                                <td className="pr-2 text-right mono">{t.exit}</td>
                                                <td className={`pr-2 text-right mono font-semibold ${toneClassName(signTone(t.pnl))}`}>
                                                    {signedPrefix(t.pnl)}{t.pnl.toLocaleString()}
                                                </td>
                                                <td className="text-muted-foreground">{t.reason}</td>
                                            </tr>
                                        ))}
                                        {result.trades.length === 0 && (
                                            <tr><td colSpan={8} className="py-4 text-center text-muted-foreground">
                                                No signals cleared the confidence threshold. Try lowering min. confidence.
                                            </td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

function Kpi({ label, value, sub, accent, icon }) {
    return (
        <div className="border border-border bg-card rounded-lg p-3">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                {icon && <span className="w-3 h-3">{icon}</span>}
                {label}
            </div>
            <div className={`text-lg sm:text-2xl font-extrabold mono mt-1 whitespace-nowrap ${accent || ""}`}>{value}</div>
            {sub && <div className="text-[10px] text-muted-foreground mt-0.5 break-words">{sub}</div>}
        </div>
    );
}

function CompareKpiRow({ label, a, b }) {
    return (
        <div className="flex items-center justify-between p-2 rounded bg-muted/40 border border-border">
            <span className="text-muted-foreground text-[11px]">{label}</span>
            <div className="flex items-center gap-2 mono">
                <span className="text-primary font-bold">{a}</span>
                <span className="text-muted-foreground">vs</span>
                <span className="text-amber-600 dark:text-amber-400 font-bold">{b}</span>
            </div>
        </div>
    );
}

// Merges two equity-curve arrays (of possibly different lengths) into a
// single dataset the LineChart can render with two lines.
function _mergeCurves(current, compare) {
    if (!compare) return (current || []).map((p) => ({ ...p, equity_current: p.equity }));
    const len = Math.max(current.length, compare.length);
    return Array.from({ length: len }, (_, i) => ({
        time:            current[i]?.time || compare[i]?.time,
        equity_current:  current[i]?.equity,
        equity_compare:  compare[i]?.equity,
    }));
}
